import React from "react";
import { Quote } from "lucide-react";
import { useTestimonials } from "../../lib/useTestimonials";
import { usePageContent } from "../../lib/usePageContent";

/**
 * Reusable testimonials strip. Renders nothing when there are zero
 * published testimonials — so admins can hide the section by unpublishing
 * every entry.
 */
export const TestimonialsSection = ({ page = "home" }) => {
    const { items, loaded } = useTestimonials();
    const { get } = usePageContent(page);

    if (!loaded) return null;
    if (!items || items.length === 0) return null;

    return (
        <section
            data-testid="testimonials-section"
            className="py-20 sm:py-28 bg-white"
        >
            <div className="max-w-6xl mx-auto px-6 sm:px-8">
                <div className="max-w-2xl mb-14">
                    <p className="font-poppins text-[11px] font-semibold uppercase tracking-[0.24em] text-green_brand">
                        {get("testimonials.eyebrow", "What leaders are saying")}
                    </p>
                    <h2 className="mt-3 font-montserrat font-bold text-indigo_brand text-3xl sm:text-4xl leading-[1.15]">
                        {get("testimonials.title", "Real teams. Real outcomes.")}
                    </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                    {items.map((t) => (
                        <figure
                            key={t.id}
                            data-testid={`testimonial-${t.id}`}
                            className="ih-card relative p-8 sm:p-10 shadow-medium flex flex-col"
                        >
                            <span
                                aria-hidden
                                className="absolute -top-4 left-6 inline-flex items-center justify-center w-9 h-9 rounded-xl bg-green_brand text-white shadow-md"
                            >
                                <Quote size={16} />
                            </span>
                            <blockquote className="font-opensans text-[15px] leading-relaxed text-[#1A2E4F] pt-2 grow">
                                &ldquo;{t.quote}&rdquo;
                            </blockquote>
                            <figcaption className="mt-6 flex items-center gap-3 pt-6 border-t border-[#EDEFF3]">
                                <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-indigo_brand text-white font-montserrat font-bold text-sm shrink-0">
                                    {t.initials || (t.author_name || "?").slice(0, 1)}
                                </span>
                                <div>
                                    <div className="font-montserrat font-semibold text-indigo_brand text-sm leading-tight">
                                        {t.author_name}
                                    </div>
                                    <div className="font-opensans text-[12px] text-[#4A5568] leading-tight">
                                        {t.author_title}
                                        {t.organization ? ` · ${t.organization}` : ""}
                                    </div>
                                </div>
                            </figcaption>
                        </figure>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default TestimonialsSection;
